import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useApi, img } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { SearchBar } from "@/components/site/SearchBar";
import { ListingCard } from "@/components/site/ListingCard";
import { StoryCard } from "@/components/site/StoryCard";
import { DirectoryCard } from "@/pages/Directory";
import { useLang } from "@/lib/i18n";

export const placePath = (p) => `/${p.kind === "activity" ? "things-to-do" : "areas"}/${p.slug}`;

export const PlaceCard = ({ p }) => {
  const { t, L, to } = useLang();
  return (
    <Link to={to(placePath(p))} className="img-zoom group relative block aspect-[4/3] overflow-hidden rounded-2xl" data-testid={`place-card-${p.slug}`}>
      <img src={img(p.cover_image)} alt={L(p, "name")} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-4 p-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sand">{t(p.kind === "activity" ? "place.activity" : "place.area")}</p>
          <h3 className="mt-1 font-display text-2xl font-black text-white">{L(p, "name")}</h3>
        </div>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-forest transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
      </div>
    </Link>
  );
};

export const Group = ({ title, children, testId }) => (
  <section className="mt-14" data-testid={testId}>
    <h2 className="text-2xl font-black tracking-tight text-forest sm:text-3xl">{title}</h2>
    <div className="mt-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </section>
);

const Results = ({ r }) => {
  const { t } = useLang();
  const sale = r.listings.filter((l) => l.kind !== "rent");
  const rent = r.listings.filter((l) => l.kind === "rent");
  const n = r.listings.length + r.directory.length + r.posts.length + r.places.length;
  return (
    <>
      <p className="mt-8 font-semibold text-moss" data-testid="search-count">{n ? t("search.results", { n, q: r.q }) : t("search.none", { q: r.q })}</p>
      {r.places.length > 0 && <Group title={t("search.guides")} testId="search-group-places">{r.places.map((p) => <PlaceCard key={p.slug} p={p} />)}</Group>}
      {sale.length > 0 && <Group title={t("search.homes")} testId="search-group-sale">{sale.map((l) => <ListingCard key={l.slug} listing={l} />)}</Group>}
      {rent.length > 0 && <Group title={t("search.stays")} testId="search-group-rent">{rent.map((l) => <ListingCard key={l.slug} listing={l} />)}</Group>}
      {r.directory.length > 0 && <Group title={t("search.dir")} testId="search-group-directory">{r.directory.map((d) => <DirectoryCard key={d.name} d={d} />)}</Group>}
      {r.posts.length > 0 && <Group title={t("search.posts")} testId="search-group-posts">{r.posts.map((p) => <StoryCard key={p.slug} post={p} />)}</Group>}
    </>
  );
};

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const { t } = useLang();
  const { data, loading } = useApi(`/search?q=${encodeURIComponent(q)}`, null);
  return (
    <>
      <Seo title={t("search.seoTitle")} description={t("search.placeholder")} noindex />
      <section className="container-x pb-28 pt-16 sm:pt-20" data-testid="search-page">
        <Reveal>
          <h1 className="text-4xl font-black tracking-tight text-forest sm:text-5xl">{t("search.title")}</h1>
          <div className="mt-8 max-w-3xl"><SearchBar initial={q} autoFocus={!q} testId="search-page-bar" /></div>
        </Reveal>
        {q && !loading && data && <Results r={data} />}
      </section>
    </>
  );
}
